import React, { useEffect, useState } from "react";
import '../CustomStyle.css';
import '../styles/ProofTemplate.css';
import { Spinner, Alert, Modal, Button } from "react-bootstrap";
import {
  constructAndReturnValidationErrorMessage,
  formatPhoneNumber,
  renderPdf,
  renameFieldNames,
  resizeImage,
  getAspectRatio,
  validateImageSize,
  validateImageType,
  computeFileChecksum,
  deepEqual,
  base64Decode,
  validateCampaignAndSaveTemplate
} from "../utils/PreviewUtils";
import { applyHighlights, fetchFont } from "../utils/highlight.js";
import { Typography } from "@mui/material";
import {
  saveCustomizationToDB,
  generatePreviewPdf,
  fetchTemplatesConfig,
  fetchTemplateConfigDetails,
  getCustomizedTemplatesCreatedByUser,
  viewPreviewPdf
} from "../utils/api";
import TemplateSelector from "../components/TemplateSelector";
import PdfPreviewModal from "../components/PdfPreviewModal";
import ReusableForm from "../components/ReusableForm";
import PageHelmet from "../components/PageHelmet";
import { savedTemplatesRowMapping, savedTemplatesGridColumns, generateGridColumns, generateGridRows } from '../utils/PageUtils'
import CustomDataGrid from '../components/CustomDataGrid'
import ViewPdfModal from '../components/ViewPdfModal'
import { FONT_BASE_URL } from "../constants";


const ProofTemplate = ({ theme, userRole, bearerToken }) => {
  const [state, setState] = useState({
    selectedTemplate: "",
    templateConfig: null,
    pdfBinaryData: {},
    cleanPdfBinaryData: {},
    isModalOpen: false,
    templateName: "",
    isLoading: false,
    pdfImageSrc: null,
    error: "",
    successMessage: "",
    pdfError: null,
    initialMessage: "Type of Template determines the content & font.",
    highlightFieldName: null,
    fontMap: {},
  });
  const [lastGeneratedParams, setLastGeneratedParams] = useState({});
  const [selectedTemplateConfig, setSelectedTemplateConfig] = useState({});
  const [formFields, setFormFields] = useState({ contact: {}, customer: {} });
  const [formData, setFormData] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [templateConfigCache, setTemplateConfigCache] = useState({});

  const [savedTemplatesError, setSavedTemplatesError] = useState(null)
  const [savedTemplates, setSavedTemplates] = useState([])
  const [refreshRequests, setRefreshRequests] = useState(false)
  const [activeTab, setActiveTab] = useState('create'); // 'view' or 'create'
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [showPDF, setShowPDF] = useState(false);
  const [savedPdfImage, setSavedPdfImage] = useState(null)
  const [savedPDF, setSavedPDF] = useState(null)
  const [templateLoading, setTemplateLoading] = useState(false)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPdfError, setShowPdfError] = useState(false);
  const [showTemplateError, setShowTemplateError] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchTemplatesForm = async () => {
      if (!bearerToken) {
        return;
      }
      setTemplatesLoading(true);
      try {
        const response = await fetchTemplatesConfig(bearerToken);
        if (response && response.status === 200) {
          const data = response.data;
          setFormData(data?.records);
        }
        else if (response.status == 404) {
          setTemplateError(response.data?.message);
          setShowTemplateError(true);
        }
        else if (response.status == 500) {
          setTemplateError("The operation you attempted has failed. Please contact support.");
          setShowTemplateError(true);
        }
        else {
          setTemplateError(response?.message || "Failed to fetch templates configuration");
          setShowTemplateError(true);
        }
      } catch (error) {
        setTemplateError("The operation you attempted has failed. Please contact support.");
        setShowTemplateError(true);
      } finally {
        setTemplatesLoading(false);
      }
    }

    fetchTemplatesForm();
  }, []);

  // Pre-select template from URL query parameter (keep dropdown visible)
  useEffect(() => {
    if (!formData?.length) {
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const templateID = queryParams.get("t");

    if (!templateID) {
      return;
    }
    let decodedTemplateId;
    try {
      decodedTemplateId = base64Decode(templateID);
    } catch (error) {
      setTemplateError(`Invalid Template URL.`);
      setShowTemplateError(true);
      return;
    }
    const matchedTemplate = formData.find(
      (t) =>
        t.unique_id === decodedTemplateId
    );

    if (!matchedTemplate) {
      setTemplateError(
        `Invalid Template URL.`
      );
      setShowTemplateError(true);
      return;
    }

    const templateValue = `${matchedTemplate?.owner}|${matchedTemplate?.name}`;

    // Remove query parameters from URL
    window.history.replaceState({}, "", `${window.location.pathname}`);

    handleSelect({
      target: {
        value: templateValue,
      },
    });
  }, [formData]);

  const validateImageAspectRatio = (file, expectedWidth, expectedHeight) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const uploadedRatio = getAspectRatio(img.naturalWidth, img.naturalHeight);
          const expectedRatio = getAspectRatio(expectedWidth, expectedHeight);

          if (uploadedRatio === expectedRatio) {
            resolve({ valid: true });
          } else {
            resolve({
              valid: false,
              message: `Invalid aspect ratio. Expected ${expectedRatio}, Got ${uploadedRatio}. `,
            });
          }
        };
        img.onerror = () => {
          resolve({ valid: false, message: "Failed to load image. Please ensure the file is a valid image." });
        };
        img.src = event.target.result;
      };
      reader.onerror = () => {
        resolve({ valid: false, message: "Failed to read the file." });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleInputChange = async (e, section, key, type, imageSize) => {
    if (type === "file" && e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const [width, height] = imageSize.split(" x ");
      const fieldKey = `${section}-${key}`;


      const imageTypeResult = await validateImageType(file);
      if (!imageTypeResult) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldKey]: "Invalid file type. Please upload a valid image file (JPEG, PNG, WEBP)."
        }));
        // Reset the file input
        e.target.value = "";
        // Clear the previously uploaded image
        setFormFields((prev) => ({
          ...prev,
          [section]: { ...(prev[section] || {}), [key]: "" },
        }));
        return;
      }

      const aspectRatioResult = await validateImageAspectRatio(file, Number(width), Number(height));

      if (!aspectRatioResult.valid) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldKey]: aspectRatioResult.message,
        }));
        // Reset the file input
        e.target.value = "";
        // Clear the previously uploaded image
        setFormFields((prev) => ({
          ...prev,
          [section]: { ...(prev[section] || {}), [key]: "" },
        }));
        return;
      }

      // Validate image size before resizing and get dimensions
      const sizeResult = await validateImageSize(file, Number(width), Number(height));

      if (!sizeResult.valid) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldKey]: sizeResult.message,
        }));
        // Reset the file input
        e.target.value = "";
        // Clear the previously uploaded image
        setFormFields((prev) => ({
          ...prev,
          [section]: { ...(prev[section] || {}), [key]: "" },
        }));
        return;
      }
      setFieldErrors((prev) => ({
        ...prev,
        [fieldKey]: null,
      }));


      //  Use dimensions from validation result instead of reading again
      const imageDimensions = sizeResult.dimensions;
      let resizedFile = file;
      // Only resize if dimensions don't match
      if (imageDimensions && (imageDimensions.width !== Number(width) || imageDimensions.height !== Number(height))) {
        const resizedBlob = await resizeImage(file, width, height, 300, 1);
        resizedFile = new File([resizedBlob], file.name, {
          type: resizedBlob.type,
        });
      }
      resizedFile.originalFile = file;
      resizedFile.imageSize = imageSize;
      resizedFile.dimensions = imageDimensions;

      // Add toJSON method to return checksum instead of full File object
      const checksum = await computeFileChecksum(resizedFile);
      resizedFile.toJSON = () => checksum;



      setFormFields((prev) => ({
        ...prev,
        [section]: { ...(prev[section] || {}), [key]: resizedFile },
      }));

    } else {
      const value = e.target.value;
      const valToSet = type === "phone" ? formatPhoneNumber(value) : value;
      setFormFields((prev) => ({
        ...prev,
        [section]: { ...(prev[section] || {}), [key]: valToSet },
      }));
    }
  };

  const handleInputFocus = async (fieldName, templateType) => {
    if (templateType !== "Postcard") return;

    setState((prev) => ({ ...prev, highlightFieldName: fieldName }));

    if (isDesktop && selectedTemplateConfig && state.selectedTemplate) {

      const currentPdfData = state.cleanPdfBinaryData[state.selectedTemplate];
      const lastParams = lastGeneratedParams[state.selectedTemplate];

      if (currentPdfData && lastParams) {
        const renamedValues = renameFieldNames(lastParams, selectedTemplateConfig);
        const combinedValues = {
          ...renamedValues.contact,
          ...renamedValues.customer,
        };

        try {
          const highlightedPDF = await applyHighlights(
            currentPdfData,
            selectedTemplateConfig,
            combinedValues,
            fieldName,
            state.fontMap
          );

          setState((prev) => ({
            ...prev,
            pdfBinaryData: {
              ...prev.pdfBinaryData,
              [state.selectedTemplate]: highlightedPDF,
            },
          }));
        } catch (error) {
          console.error("Error applying highlights:", error);
        }
      }
    }
  };

  const handleSelect = async (e) => {
    const fullValue = e.target.value;
    if (!fullValue) return;

    const [owner, templateName] = fullValue.split('|');
    const templateMeta = formData.find(t => t.name === templateName && t.owner === owner);

    if (!templateMeta) return;

    setState(prev => ({
      ...prev,
      selectedTemplate: fullValue,
      initialMessage: "",
      pdfError: null,
      isModalOpen: false,
      pdfImageSrc: null,
    }));

    const cacheKey = fullValue;

    if (templateConfigCache[cacheKey]) {
      const { template, values } = templateConfigCache[cacheKey];
      setFormFields(values);
      setSelectedTemplateConfig(template);
      if (isDesktop) {
        handleGeneratePreviewInternal(template, values, fullValue);
      }
      return;
    }

    try {
      setIsConfigLoading(true);

      const response = await fetchTemplateConfigDetails(
        owner,
        templateName,
        bearerToken
      );

      if (response?.status === 200) {
        setShowTemplateError(false);
        const template = response.data;
        const values = {};
        const sections = {
          contact: template.contact || [],
          customer: template.customer || [],
        };

        Object.entries(sections).forEach(([key, value]) => {
          values[key] = value.reduce((acc, field) => {
            acc[field.name] = field.defaultValue ?? "";
            return acc;
          }, {});
        });

        setFormFields(values);
        setSelectedTemplateConfig(template);
        setTemplateConfigCache((prev) => ({
          ...prev,
          [cacheKey]: { template, values },
        }));
        if (isDesktop) {
          handleGeneratePreviewInternal(template, values, fullValue);
        }
      }
      else if (response.status == 400) {
        setTemplateError(response.data?.message);
        setShowTemplateError(true);
      }
      else if (response.status == 403) {
        setTemplateError(response.data?.message);
        setShowTemplateError(true);
      }
      else if (response.status == 404) {
        setTemplateError(response.data?.message);
        setShowTemplateError(true);
      }
      else if (response.status == 500) {
        setTemplateError("The operation you attempted has failed. Please contact support.");
        setShowTemplateError(true);
      }
      else {
        setTemplateError("The operation you attempted has failed. Please contact support.");
        setShowTemplateError(true);
      }
    } catch (err) {
      setTemplateError("The operation you attempted has failed. Please contact support.");
      setShowTemplateError(true);
    } finally {
      setIsConfigLoading(false);
    }
  };

  const trimTemplateData = (templateData) => {
    const trimmed = {};
    for (const sectionKey in templateData) {
      trimmed[sectionKey] = {};
      for (const fieldKey in templateData[sectionKey]) {
        const value = templateData[sectionKey][fieldKey];
        trimmed[sectionKey][fieldKey] =
          typeof value === "string" ? value.trim() : value;
      }
    }
    return trimmed;
  };

  const handleGeneratePreviewInternal = async (config, fields, templateId) => {
    const trimmedValues = trimTemplateData(fields);
    setFormFields(trimmedValues);
    const filterEmptyFileFields = (section, values) => {
      const result = { ...values };
      const fieldConfigs = config[section] || [];
      fieldConfigs.forEach(field => {
        if (field.type === 'file') {
          const val = result[field.name];
          // If value is empty, null, undefined, or matches default value, remove it
          if (!val || (typeof val === 'string' && val.trim() === '')) {
            delete result[field.name];
          }
        }
      });
      return result;
    };

    const queryParams = {
      name: config.name,
      owner: config.owner,
      contact: filterEmptyFileFields('contact', trimmedValues.contact),
      customer: filterEmptyFileFields('customer', trimmedValues.customer)
    };

    const existingPdf = state.pdfBinaryData[templateId];

    // Check if we can serve from cache
    if (deepEqual(queryParams, lastGeneratedParams[templateId]) && existingPdf) {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: null,
      }));

      // Small delay to show the "Generating" spinner so the user knows it's working
      setTimeout(() => {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isModalOpen: true,
        }));
      }, 300);
      return;
    }

    // Otherwise, fetch new preview
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
      pdfError: null,
      successMessage: null,
    }));

    try {
      const newPdfContent = await generatePreviewPdf(queryParams, bearerToken);
      if (!newPdfContent.success) {
        setShowPdfError(true);
        setState((prevState) => ({
          ...prevState,
          pdfError: newPdfContent.message,
          isLoading: false,
          isModalOpen: false,
          pdfBinaryData: {
            ...prevState.pdfBinaryData,
            [templateId]: null,
          },
          cleanPdfBinaryData: {
            ...prevState.cleanPdfBinaryData,
            [templateId]: null,
          },

        }));
        return;
      }
      setState((prevState) => ({
        ...prevState,
        pdfBinaryData: {
          ...prevState.pdfBinaryData,
          [templateId]: newPdfContent.data,
        },
        cleanPdfBinaryData: {
          ...prevState.cleanPdfBinaryData,
          [templateId]: newPdfContent.data,
        },
        pdfImageSrc: null,
      }));

      setLastGeneratedParams((prev) => ({
        ...prev,
        [templateId]: queryParams,
      }));
    } catch (error) {
      setShowPdfError(true);
      setState((prevState) => ({
        ...prevState,
        pdfError: error.message,
        isLoading: false,
        isModalOpen: false,
        pdfBinaryData: {
          ...prevState.pdfBinaryData,
          [templateId]: null,
        },
        cleanPdfBinaryData: {
          ...prevState.cleanPdfBinaryData,
          [templateId]: null
        }
      }));
    }
  };

  // --- Font Loading Logic ---

  useEffect(() => {
    const loadRequiredFonts = async () => {
      if (!selectedTemplateConfig || (selectedTemplateConfig.type !== "Postcard")) return;

      const requiredFonts = new Set();
      const allFields = [...(selectedTemplateConfig.contact || []), ...(selectedTemplateConfig.customer || [])];

      allFields.forEach(field => {
        if (field.fontName) requiredFonts.add(field.fontName);
        if (field.config) {
          field.config.forEach(c => {
            if (c.fontName) requiredFonts.add(c.fontName);
          });
        }
      });

      const newFontMap = { ...state.fontMap };
      let updated = false;

      for (const fontName of requiredFonts) {
        if (!newFontMap[fontName]) {
          try {
            // Dynamically construct URL: BASE_URL/FontName.ttf
            const fontUrl = `${FONT_BASE_URL}/${encodeURIComponent(fontName)}.ttf`;
            const fontData = await fetchFont(fontUrl);
            newFontMap[fontName] = fontData;
            updated = true;
          } catch (err) {
            console.error(`Failed to load font ${fontName}:`, err);
          }
        }
      }

      if (updated) {
        setState(prev => ({ ...prev, fontMap: newFontMap }));
      }
    };

    loadRequiredFonts();
  }, [selectedTemplateConfig]);

  const handleGeneratePreview = () => {
    handleGeneratePreviewInternal(selectedTemplateConfig, formFields, state.selectedTemplate);
  };

  useEffect(() => {
    const renderPdfPreview = async () => {
      if (!state.pdfBinaryData[state.selectedTemplate]) return;
      try {
        const pdfImages = await renderPdf(
          state.pdfBinaryData[state.selectedTemplate]
        );
        setState((prevState) => ({
          ...prevState,
          pdfImageSrc: pdfImages,
        }));
      } catch (error) {
        console.log("✗ Error rendering PDF preview:", error.message);
        setShowPdfError(true);
        setState((prevState) => ({
          ...prevState,
          pdfError: "Error rendering PDF preview"
        }));
      }
    };
    renderPdfPreview();
  }, [state.pdfBinaryData, state.selectedTemplate]);

  useEffect(() => {
    if (state.pdfBinaryData && state.pdfBinaryData[state.selectedTemplate]) {
      setState((prevState) => ({
        ...prevState,
        isModalOpen: true,
        isLoading: false,
      }));
    }
  }, [state.pdfBinaryData]);

  const handleClose = () =>
    setState((prevState) => ({ ...prevState, isModalOpen: false }));

  const handleClosePdfError = () => {
    setShowPdfError(false);
  };

  const handleCloseTemplateError = () => {
    setShowTemplateError(false);
  };

  const handleTemplateSave = async () => {
    setState((prevState) => ({
      ...prevState,
      error: null,
      successMessage: null,
      templateName: "",
    }));

    const trimmedTemplateName = state.templateName?.trim();

    const validationResult = validateCampaignAndSaveTemplate(trimmedTemplateName, "Template")
    if (validationResult.error) {
      setState((prevState) => ({
        ...prevState,
        error: validationResult.message,
      }));
      return;
    }

    try {
      const prepareSectionForSave = async (sectionName) => {
        const values = { ...formFields[sectionName] };
        for (const key in values) {
          const val = values[key];
          if (val instanceof File && val.originalFile && val.imageSize) {
            const [w, h] = val.imageSize.split(" x ");
            // Use cached dimensions to avoid re-reading the file
            const imageDimensions = val.dimensions;

            // Only resize if dimensions don't match
            if (imageDimensions && (imageDimensions.width !== Number(w) || imageDimensions.height !== Number(h))) {
              const blob = await resizeImage(val.originalFile, w, h, 300, 1);
              values[key] = new File([blob], val.name, {
                type: blob.type,
              });
            } else {
              values[key] = val.originalFile;
            }
          }
        }
        return values;
      };

      const data = {
        contact: await prepareSectionForSave("contact"),
        customer: await prepareSectionForSave("customer"),
        template: selectedTemplateConfig.name,
        name: trimmedTemplateName,
        owner: selectedTemplateConfig.owner,
      };


      setState((prevState) => ({
        ...prevState,
        successMessage: null,
        error: null,
      }));
      const response = await saveCustomizationToDB(data, bearerToken);
      switch (response.status) {
        case 201:
          setState((prevState) => ({
            ...prevState,
            successMessage:
              "Template saved successfully",
            templateName: "",
          }));
          break;
        case 400:
          setState((prevState) => ({
            ...prevState,
            error:
              "The operation you attempted has failed. Please contact support.",
          }));
          break;
        case 403:
          setState((prevState) => ({
            ...prevState,
            error: `Quota exceeded for ${response.data.details.action}: Limit ${response.data.details.limit}, Consumed ${response.data.details.current_usage}, Available ${response.data.details.limit_available}.\nContact Support to increase your Quota.`
          }));
          break;
        case 409:
          const message = `A template already exists for the given name ${state.templateName}. Please choose another name for this template.`;
          setState((prevState) => ({
            ...prevState,
            error: message,
          }));
          break;
        case 422:
          const constructErrorMesage = constructAndReturnValidationErrorMessage(
            response.data,
            "Your attempt to create a template failed due to invalid inputs"
          );
          setState((prevState) => ({
            ...prevState,
            error: constructErrorMesage,
          }));
          break;

        default:
          setState((prevState) => ({
            ...prevState,
            error:
              "The operation you attempted has failed. Please contact support.",
          }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        error: error.message,
      }));
    }
  };

  const handleDownloadPdf = () => {
    const pdfData = state.cleanPdfBinaryData[state.selectedTemplate];
    if (!pdfData) return;

    try {
      const blob = new Blob([pdfData], { type: "application/pdf" });
      const link = document.createElement("a");
      const fileName = `${selectedTemplateConfig.owner}_${selectedTemplateConfig.name || "template"}_preview`;

      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  useEffect(() => {
    const fetchSavedTemplates = async () => {
      setSavedTemplatesError(null)
      try {
        const response = await getCustomizedTemplatesCreatedByUser(bearerToken);
        if (!Array.isArray(response)) {
          setSavedTemplatesError(
            "Please create a customized template first and then come back here"
          );
          setSavedTemplates([]);
          return;
        }
        else {
          const sortedResponse = response.toSorted((a, b) => new Date(b.created) - new Date(a.created));
          setSavedTemplates(sortedResponse)
        }
      } catch {
        setSavedTemplatesError("The operation you attempted has failed. Please contact support.")
      }
    }
    if (refreshRequests) {
      fetchSavedTemplates()
      setRefreshRequests(false)
    }
  }, [refreshRequests]);

  useEffect(() => {
    const showSavedPDF = async () => {
      setSavedTemplatesError(null)
      if (!savedPDF) return;

      try {
        const imageUrls = await renderPdf(savedPDF);
        setSavedPdfImage(imageUrls);
        setShowPDF(true);
        setTemplateLoading(false)
      } catch {
        setSavedTemplatesError("The operation you attempted has failed. Please contact support.")
        setTemplateLoading(false)
      }
    }
    showSavedPDF();
  }, [savedPDF])

  const handleViewTemplatesClick = () => {
    setActiveTab('view');
    setRefreshRequests(true)
  }

  const handleCreateTemplatesClick = () => {
    setActiveTab('create');
  }

  const handleRowClick = (e) => {
    setSavedTemplatesError(null)
    if (selectedRowId === e.id) {
      setSelectedRowId(null)
    } else {
      setSelectedRowId(e.id)
    }
  }

  const handleViewPDF = async (selectedTemplate) => {
    setSavedTemplatesError(null)
    setTemplateLoading(true)
    if (!selectedTemplate) {
      setSavedTemplatesError("Please select a template to view the PDF.")
      setTemplateLoading(false)
      return;
    }
    const queryParams = {
      template_name: selectedTemplate.template_name,
      name: selectedTemplate.choice,
      owner: selectedTemplate.owner,
    }

    const pdfResponse = await viewPreviewPdf(queryParams, bearerToken)
    if (!pdfResponse.success) {
      setSavedTemplatesError(pdfResponse.message)
      setTemplateLoading(false)
      return
    }
    setSavedPDF(pdfResponse.data)
  }

  const closeShowPDF = () => {
    setShowPDF(false)
  }

  const savedTemplatesRows = generateGridRows(savedTemplates, savedTemplatesRowMapping)
  const savedTemplatesColumns = generateGridColumns(savedTemplatesGridColumns, 'template_name', handleViewPDF, {})

  useEffect(() => {
    if (state.pdfImageSrc && isDesktop) {
      const element = document.querySelector(".proof-flex-container");
      if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [state.pdfImageSrc, isDesktop]);

  const sharedModalProps = {
    isModalOpen: state.isModalOpen,
    handleClose,
    pdfImageSrc: state.pdfImageSrc,
    selectedTemplate: state.selectedTemplate,
    handleDownloadPdf,
    error: state.error,
    successMessage: state.successMessage,
    templateName: state.templateName,
    setTemplateName: (name) => setState((prevState) => ({ ...prevState, templateName: name })),
    handleTemplateSave,
    templateType: selectedTemplateConfig?.type,
    templateID: selectedTemplateConfig?.unique_id
  };


  return userRole === "customer" ? (
    <div className="dashboard-container">
      <PageHelmet
        title="Proof Template"
        description="Choose and Save templates with TraQR."
        url={`${process.env.REACT_APP_REDIRECT_URI}/proof_templates`}
      />
      <div className="proof-title-section">
        <h1 className="main-heading">Create & Proof Your Template</h1>
      </div>

      <div className={`templates-tab ${activeTab === "view" ? "view-active" : ""}`}>
        <button onClick={handleCreateTemplatesClick} className={`tab-button ${activeTab === "create" ? "active" : ""}`}>
          Create Template
        </button>
        <button onClick={handleViewTemplatesClick} className={`tab-button ${activeTab === "view" ? "active" : ""}`}>
          Saved Templates
        </button>
      </div>

      {/* Global Error Display - Modal */}
      <Modal
        show={(state.pdfError && showPdfError) || (templateError && showTemplateError)}
        onHide={() => {
          if (state.pdfError && showPdfError) handleClosePdfError();
          if (templateError && showTemplateError) handleCloseTemplateError();
        }}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Error(s) : </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: "pre-line", color: '#333' }}>
          {state.pdfError || templateError}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => {
            if (state.pdfError && showPdfError) handleClosePdfError();
            if (templateError && showTemplateError) handleCloseTemplateError();
          }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {activeTab === 'view' && (
        <>
          {templateLoading && (
            <div className='pdf-loading'>
              <Spinner animation="border" role="status" />
              <p>Saved Template Loading...</p>
            </div>
          )}
          {savedTemplatesError && (
            <div style={{ marginTop: '15px' }}>
              <Alert variant='danger'>{savedTemplatesError}</Alert>
            </div>
          )}
          <div className='saved-templates-section'>
            <CustomDataGrid
              rows={savedTemplatesRows}
              columns={savedTemplatesColumns}
              totalRows={savedTemplates.length}
              onRowClick={handleRowClick}
              selectedRowId={selectedRowId}
            />
            <ViewPdfModal
              isModalOpen={showPDF}
              handleClose={closeShowPDF}
              pdfImageSrc={savedPdfImage}
            />
          </div>
        </>
      )}

      {activeTab === 'create' && (
        <>
          <div className={`proof-flex-container ${state.selectedTemplate ? "template-selected" : ""}`}>
            <div className="proof-left-column">
              {templatesLoading ? (
                <div className="template-loading-container">
                  <Spinner animation="border" variant="primary" />
                  <p>Loading templates...</p>
                </div>
              ) : (
                <TemplateSelector
                  selectedTemplate={state.selectedTemplate}
                  handleSelect={handleSelect}
                  templates={formData}
                />
              )}
              {isConfigLoading && (
                <div className="template-loading-container">
                  <Spinner animation="border" variant="primary" />
                  <p>Loading Template Form...</p>
                </div>
              )}

              <div className="proof-form-side">
                {state.selectedTemplate &&
                  selectedTemplateConfig &&
                  Object.keys(selectedTemplateConfig).length > 0 &&
                  Object.values(selectedTemplateConfig).some(value => Array.isArray(value) && value.length > 0) && (
                    <div className="proof-form-container">
                      <ReusableForm
                        selectedTemplateConfig={selectedTemplateConfig}
                        formFields={formFields}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        handleGeneratePreview={handleGeneratePreview}
                        highlightFieldName={state.highlightFieldName}
                        fieldErrors={fieldErrors}
                      />
                      {!isDesktop && state.isLoading && (
                        <div className="template-loading-container" style={{ marginTop: '15px' }}>
                          <Spinner animation="border" variant="primary" size="sm" />
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Generating {selectedTemplateConfig.name} preview...
                          </Typography>
                        </div>
                      )}
                    </div>
                  )}


              </div>
            </div>

            {/* ── DESKTOP: PdfPreviewModal owns header + PDF ── */}
            {isDesktop && state.selectedTemplate && (
              <div className="proof-right-side-layout">
                {state.isLoading ? (
                  <div className="side-loading-container">
                    <Spinner animation="border" variant="primary" />
                    <p>Generating {selectedTemplateConfig.name} preview...</p>
                  </div>
                ) : state.isModalOpen && (
                  <PdfPreviewModal {...sharedModalProps} isDesktop={true} />
                )}
              </div>
            )}

            {!isDesktop && state.selectedTemplate && (
              <PdfPreviewModal {...sharedModalProps} isDesktop={false} />
            )}
          </div>
        </>
      )}
    </div>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  );
};
export default ProofTemplate;